/**
 * Agent Insights Panel
 * Displays AI-generated insights for documents
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Brain, AlertTriangle, CheckCircle, Clock, RefreshCw, TrendingUp, Shield } from 'lucide-react';
import agenticApi from '../../api/agenticApi';

const AgentInsightsPanel = ({ docId, language = 'en', headers }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInsights = async () => {
    if (!docId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await agenticApi.getDocumentInsights(docId, language, headers);
      setInsights(response.data);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
      setError(err.response?.data?.detail || 'Failed to load AI insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [docId, language]);

  const getRiskBadge = (risk) => {
    const colors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    return colors[risk] || colors.medium;
  };

  const getComplianceBadge = (status) => {
    const colors = {
      compliant: 'bg-green-100 text-green-800',
      review_needed: 'bg-yellow-100 text-yellow-800',
      non_compliant: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.review_needed;
  };

  if (loading) {
    return (
      <Card className="border-blue-200 bg-blue-50/30">
        <CardContent className="py-8 text-center">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-600 mb-2" />
          <p className="text-sm text-blue-600">Analyzing document with AI...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-amber-200 bg-amber-50/30">
        <CardContent className="py-4 text-center">
          <AlertTriangle className="w-5 h-5 mx-auto text-amber-600 mb-2" />
          <p className="text-sm text-amber-700">{error}</p>
          <Button variant="outline" size="sm" className="mt-2" onClick={fetchInsights}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!insights) return null;

  const { insights: data, approval_probability } = insights;

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50/50 to-blue-50/50" data-testid="agent-insights-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Brain className="w-5 h-5 text-purple-600" />
          AI Document Insights
          <Badge variant="outline" className="ml-auto text-xs">
            Powered by Agentic AI
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        <div className="p-3 bg-white rounded-lg border">
          <p className="text-sm text-slate-700">{data?.summary || 'No summary available'}</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3">
          {/* Approval Risk */}
          <div className="p-3 bg-white rounded-lg border">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-slate-500" />
              <span className="text-xs text-slate-500">Approval Risk</span>
            </div>
            <Badge className={getRiskBadge(data?.approval_risk)}>
              {data?.approval_risk?.toUpperCase() || 'UNKNOWN'}
            </Badge>
          </div>

          {/* Approval Probability */}
          <div className="p-3 bg-white rounded-lg border">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-slate-500" />
              <span className="text-xs text-slate-500">Approval Probability</span>
            </div>
            <span className="text-lg font-semibold text-slate-800">
              {approval_probability?.approval_probability || '--'}%
            </span>
          </div>

          {/* Compliance Status */}
          <div className="p-3 bg-white rounded-lg border">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-slate-500" />
              <span className="text-xs text-slate-500">Compliance</span>
            </div>
            <Badge className={getComplianceBadge(data?.compliance_check?.status)}>
              {data?.compliance_check?.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'}
            </Badge>
          </div>

          {/* Priority Score */}
          <div className="p-3 bg-white rounded-lg border">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-slate-500" />
              <span className="text-xs text-slate-500">Priority</span>
            </div>
            <span className="text-lg font-semibold text-slate-800">
              {data?.priority_score || '--'}/10
            </span>
          </div>
        </div>

        {/* Risk Factors */}
        {data?.risk_factors?.length > 0 && (
          <div className="p-3 bg-white rounded-lg border">
            <h4 className="text-sm font-medium text-slate-700 mb-2">Risk Factors</h4>
            <ul className="space-y-1">
              {data.risk_factors.map((factor, i) => (
                <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                  <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                  {factor}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Missing Metadata */}
        {data?.missing_metadata?.length > 0 && (
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="text-sm font-medium text-amber-800 mb-2">Missing Metadata</h4>
            <div className="flex flex-wrap gap-1">
              {data.missing_metadata.map((field, i) => (
                <Badge key={i} variant="outline" className="text-xs bg-white">
                  {field}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Workflow Prediction */}
        {data?.workflow_prediction && (
          <div className="p-3 bg-white rounded-lg border">
            <h4 className="text-sm font-medium text-slate-700 mb-2">Workflow Prediction</h4>
            <div className="text-xs text-slate-600 space-y-1">
              <p>Est. completion: <strong>{data.workflow_prediction.estimated_completion_days} days</strong></p>
              {data.workflow_prediction.recommended_actions?.length > 0 && (
                <div className="mt-2">
                  <span className="text-slate-500">Recommended actions:</span>
                  <ul className="mt-1 space-y-0.5">
                    {data.workflow_prediction.recommended_actions.map((action, i) => (
                      <li key={i}>• {action}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Refresh Button */}
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full text-purple-600 hover:text-purple-700 hover:bg-purple-50"
          onClick={fetchInsights}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh Insights
        </Button>
      </CardContent>
    </Card>
  );
};

export default AgentInsightsPanel;
